import type { ReactNode } from "react";
import { useRef } from "react";
import type { RecycleItem } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { Async, Failure } from "../components/Async.jsx";
import { IconState } from "../components/Icons.jsx";
import { longDate } from "../lib/format.js";

/**
 * Everything deleted and not yet destroyed.
 *
 * WHY THIS SCREEN EXISTS AT ALL, when deleting happens out in the portal where
 * the thing lives. Because a thirty-day promise nobody can see is not a
 * promise. Without a list, "you can get it back" means "ask somebody who knows
 * the database", and the first person to delete the wrong survey finds that
 * out at the worst moment.
 *
 * THE PURGE RUNS WHEN THIS SCREEN OPENS. Nothing on the server runs on a
 * timer, so a bin that waited for one would fill up for ever while claiming a
 * thirty-day life. Opening the bin empties what is due. It is not elegant and
 * it is honest, which is the trade section 6 of the privacy policy already
 * makes out loud about retention generally.
 */
export function RecycleBin(): ReactNode {
  const { client } = usePortal();
  const purged = useRef(false);

  const { state, reload } = useAsync(async () => {
    // Once per mount. Opening the bin is the trigger; re-reading it after a
    // restore is not, or a restore would race the purge it just triggered.
    if (!purged.current) {
      purged.current = true;
      await client.purgeRecycleBin().catch(() => undefined);
    }
    return client.recycleBin();
  }, [client]);

  return (
    <>
      <div className="row-between">
        <div>
          <h1>Recycle bin</h1>
          <p className="lede">
            Deleted items are kept for thirty days, then destroyed for good.
          </p>
        </div>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      <Async state={state} what="the recycle bin" reload={reload}>
        {({ items }) =>
          items.length === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph">
                <IconState kind="empty" />
              </span>
              <p className="state__title">Nothing has been deleted</p>
              <p className="state__body">
                Anything you delete from the portal waits here for thirty days
                before it is destroyed.
              </p>
            </div>
          ) : (
            <ul className="plain-list">
              {items.map((item) => (
                <BinRow key={`${item.kind}:${item.id}`} item={item} reload={reload} />
              ))}
            </ul>
          )
        }
      </Async>
    </>
  );
}

const NOUNS: Record<RecycleItem["kind"], string> = {
  survey: "Survey",
  photograph: "Photograph",
  bill: "Electricity bill",
  scan: "Scan",
  drawing: "Drawing",
  document: "Document",
};

function BinRow({
  item,
  reload,
}: {
  readonly item: RecycleItem;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();

  const restore = (): void => {
    void submit(async () => {
      await client.restoreFromRecycleBin(item.kind, item.id);
      reload();
    });
  };

  return (
    <li className="row-between">
      <span>
        <strong>{item.label}</strong>
        <span className="small muted">
          {" "}
          — {NOUNS[item.kind].toLowerCase()}, deleted {longDate(item.deletedAt)}
          {item.deletedBy ? ` by ${item.deletedBy}` : ""}.
        </span>
        <br />
        <span className="small muted">
          <Countdown purgeAt={item.purgeAt} />
          {item.reason ? ` · "${item.reason}"` : ""}
        </span>
        {error ? <Failure error={error} what="that item" /> : null}
      </span>
      <button type="button" className="secondary" onClick={restore} disabled={busy}>
        {busy ? "Putting back…" : "Put back"}
      </button>
    </li>
  );
}

/**
 * How long is left, in days.
 *
 * Said as a number of days rather than a date, because "destroyed in 2 days"
 * is read as urgent and "destroyed on 14 November" is read as a detail. The
 * date is there as well for anybody who wants to plan around it.
 */
function Countdown({ purgeAt }: { readonly purgeAt: string }): ReactNode {
  const days = Math.ceil((new Date(purgeAt).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return <>Due to be destroyed; it will go the next time this screen opens.</>;
  return (
    <>
      Destroyed in {days} {days === 1 ? "day" : "days"} ({longDate(purgeAt)}).
    </>
  );
}
